import React, { useState, useEffect, useCallback } from 'react';
import './navigator.scss';
import {
  isCtrlKeyPressed,
  isNullOrUndefined,
  SetNavigatorStatus,
} from '../../../utils/Global';

import ImagedWellCam from 'Icons/Exp_Sidebar/ImagedWellCam.svg';
import { setIsMoveToCompareTab } from 'redux/reducers/experimentReducer';
import { NavigatorMapDTO } from 'business/models/Models';
import { useRMDispatch, useRMSelector } from 'redux/hooks';
import { selectExpSaved, selectROIs } from 'redux/selectors/StateSelectors';
import ExperimentService from 'business/services/ExperimentService';
import { selectExp } from 'redux/selectors/ExperimentStateSelector';
import NavigatorSidePanel from './NavigatorSidePanel';
import {
  useAllDropImageParameters,
  useSelectedDropImageParameters,
  useLastSelectedWellNavigationDetails,
} from 'redux/hooks/experiment';
import { DropImageParameters } from 'components/Experiment/constants';
import { setWellImagingSettingDetails } from 'redux/reducers/experimentDataReducer';
import { useWellDropCompareMode } from 'redux/hooks/well';
import {
  MAX_COMPARE_IMAGE_SELECTION,
  MAX_COMPARE_OVERLAY_IMAGE_SELECTION,
} from 'components/well/constants';
import { useUpdateCompositeImagesHandler } from 'components/Experiment/hooks';

let compositeNavMap;

const imagedWellCamTag = <img src={ImagedWellCam} alt="Imaged" />;
const imagedPlateCamTag = (
  <img
    src={ImagedWellCam}
    alt="PlateImaged"
    style={{ filter: 'grayscale(100%)', opacity: 0.5 }}
  />
);
let navigatorThumbnailTabIndex = 0;

export default function Navigator() {
  const [refreshUI, setRefreshUI] = useState(false);
  const [moveToCanvas, setMoveToCanvas] = useState(false);
  const [thTabIndex, setThTabIndex] = useState(navigatorThumbnailTabIndex);
  const [inspTRs, setInspTRs] = useState([]);
  const [dcrTRs, setDcrTRs] = useState([]);
  const [lastForcedFetch, setLastForcedFetch] = useState(0);
  const dispatch = useRMDispatch();
  const _selectExpSaved = useRMSelector(selectExpSaved);
  const exp = useRMSelector(selectExp);
  const wellNavigationDetails = useLastSelectedWellNavigationDetails();
  const selectedDropImageParameters = useSelectedDropImageParameters();
  const allWellImagingParameterDetails = useAllDropImageParameters();

  const { wellNum, plateID } = wellNavigationDetails || {};
  const { cpId, dropNumber, inspection, regionName } = selectedDropImageParameters || {};

  const rois = useRMSelector((state) => selectROIs(state, dropNumber));
  const updateCompositeImages = useUpdateCompositeImagesHandler();
  const _setIsMoveToCompareTab = useCallback(
    (moveToCompare: boolean) => dispatch(setIsMoveToCompareTab(moveToCompare)),
    [dispatch],
  );
  const { isCompareModeOverlay } = useWellDropCompareMode();

  useEffect(() => {
    SetNavigatorStatus(true);

    return () => {
      SetNavigatorStatus(false);
    };
  }, []);

  function compositNavSelectionChanged(
    plateId,
    wellNumber,
    _dropNumber,
    regionName,
    cpId,
    inspSet,
    e,
  ) {
    const ctrlKey = isCtrlKeyPressed(e);
    const newWellDetails: DropImageParameters = {
      dropNumber: _dropNumber,
      regionName: regionName,
      cpId: cpId,
      inspection: inspSet,
    };
    updateCompositeImages(wellNavigationDetails, newWellDetails);
    const selectedNav = allWellImagingParameterDetails.find(
      (w) =>
        w.dropNumber === _dropNumber &&
        w.cpId === cpId &&
        w.inspection.id === inspSet.id &&
        w.regionName === regionName,
    );

    if (
      allWellImagingParameterDetails.length === 1 &&
      !isNullOrUndefined(selectedNav) &&
      ctrlKey
    ) {
      return;
    }
    const filteredNavDetails = allWellImagingParameterDetails.filter(
      (w) =>
        !(
          w.dropNumber === _dropNumber &&
          w.cpId === cpId &&
          w.inspection.id === inspSet.id &&
          w.regionName === regionName &&
          plateID === plateId
        ),
    );
    if (!isNullOrUndefined(selectedNav) && ctrlKey) {
      dispatch(setWellImagingSettingDetails([...filteredNavDetails]));
      return;
    }
    if (ctrlKey) {
      const maxSelection = isCompareModeOverlay
        ? MAX_COMPARE_OVERLAY_IMAGE_SELECTION
        : MAX_COMPARE_IMAGE_SELECTION;
      if (isCompareModeOverlay && allWellImagingParameterDetails.length >= maxSelection) {
        dispatch(
          setWellImagingSettingDetails([
            ...allWellImagingParameterDetails.slice(1),
            newWellDetails,
          ]),
        );
      } else if (allWellImagingParameterDetails.length < maxSelection) {
        dispatch(setWellImagingSettingDetails([...filteredNavDetails, newWellDetails]));
      }
      setMoveToCanvas(true);
    } else {
      dispatch(setWellImagingSettingDetails([newWellDetails]));
    }
  }

  function getInspectedWellByInspSet(drci, inspSet) {
    let plateInsp = inspSet.groupedInspections.find((insp) => insp.plateId == plateID);
    let inspWell;
    if (plateInsp != undefined) {
      inspWell = getInspectedWellByInsp(
        drci,
        plateInsp.imageBatchId,
        plateInsp.plateId,
        wellNum,
      );
      if (inspWell != undefined) {
        return inspWell;
      }
    }
    for (plateInsp of inspSet.groupedInspections) {
      inspWell = getAnyInspectedWellByInsp(
        drci,
        plateInsp.imageBatchId,
        plateInsp.plateId,
      );
      if (inspWell != undefined) {
        return inspWell;
      }
    }
    return undefined;
  }

  function getInspectedWellByInsp(drci, imgBatchId, plateId, wellNumber) {
    const inspWell = drci.inspectedWells.find(
      (inspWell) => inspWell.inspectionId == imgBatchId,
    );
    if (inspWell === undefined) {
      return undefined;
    }
    if (inspWell.wells.length === 0) {
      return undefined;
    } // no well found inspected for given drci
    const found = inspWell.wells.find(
      (w) => w.plateId == plateId && w.wellNumber == wellNumber,
    );
    return found;
  }

  function getAnyInspectedWellByInsp(drci, imgBatchId, plateId) {
    const inspWell = drci.inspectedWells.find(
      (inspWell) => inspWell.inspectionId == imgBatchId,
    );
    if (inspWell === undefined) {
      return undefined;
    }
    if (inspWell.wells.length === 0) {
      return undefined;
    } // no well found inspected for given drci
    return inspWell.wells[0];
  }

  function getCPName(cpId, profileList) {
    const found = profileList.find((cp) => cp.id == cpId);
    return found.name;
  }

  function WellInfoRendrer(inspectedWell, dcr, inspSet, index) {
    let wellCamTag;
    const active =
      dcr.dropNumber == dropNumber &&
      dcr.regionName == regionName &&
      dcr.cpId == cpId &&
      inspSet.id == inspection.id;
    if (inspectedWell) {
      const maxSelection = isCompareModeOverlay
        ? MAX_COMPARE_OVERLAY_IMAGE_SELECTION
        : MAX_COMPARE_IMAGE_SELECTION;
      const detailsToCheck = isCompareModeOverlay
        ? allWellImagingParameterDetails.slice(-maxSelection)
        : allWellImagingParameterDetails;
      const prevSelected = detailsToCheck.find(
        (w) =>
          w.dropNumber == dcr.dropNumber &&
          w.regionName == dcr.regionName &&
          w.cpId == dcr.cpId &&
          w.inspection.id == inspSet.id,
      );
      if (
        inspectedWell.plateId == plateID &&
        inspectedWell.wellNumber == wellNum &&
        allWellImagingParameterDetails.length < maxSelection
      ) {
        wellCamTag = imagedWellCamTag;
      } else if (
        (allWellImagingParameterDetails.length === maxSelection &&
          detailsToCheck.findIndex(
            (d) =>
              d.cpId === dcr.cpId &&
              d.dropNumber === dcr.dropNumber &&
              d.regionName === dcr.regionName &&
              d.inspection.id === inspSet.id,
          ) >= 0) ||
        active
      ) {
        wellCamTag = imagedWellCamTag;
      } else if (
        inspectedWell.plateId != plateID ||
        inspectedWell.wellNumber != wellNum ||
        allWellImagingParameterDetails.length === maxSelection
      ) {
        wellCamTag = imagedPlateCamTag;
      }

      return (
        <td
          id={`navigator-wellinforendrer-td-${inspectedWell?.plateId ?? 'na'}-${
            inspectedWell?.wellNumber ?? 'na'
          }-${index}`}
          className={`${
            active ? 'selected-td' : prevSelected ? 'prev-selected-td' : 'current-td'
          } 
          ${index % 2 === 0 ? 'even-td' : 'odd-td'}`}
          onClick={(e) =>
            compositNavSelectionChanged(
              inspectedWell.plateId,
              inspectedWell.wellNumber,
              dcr.dropNumber,
              dcr.regionName,
              dcr.cpId,
              inspSet,
              e,
            )
          }
        >
          {wellCamTag}
        </td>
      );
    } else {
      return (
        <td
          id={`navigator-wellinforendrer-emptycell-${index}`}
          align="center"
          className={`${active ? 'selected-td' : 'current-td'} ${
            index % 2 === 0 ? 'even-td' : 'odd-td'
          }`}
        ></td>
      );
    }
  }

  function CompositeNavInspectedWellInfoRendrer() {
    if (
      compositeNavMap === undefined ||
      compositeNavMap.inspectionSetList === undefined
    ) {
      return;
    }
    setInspTRs(
      compositeNavMap.inspectionSetList.map((inspSet) => {
        let colIndex = 1;
        const wellTDs = compositeNavMap.drciList.map((drci) => {
          const inspectedWell = getInspectedWellByInspSet(drci, inspSet);
          const wellTD = WellInfoRendrer(inspectedWell, drci.drc, inspSet, colIndex++);
          return wellTD;
        });
        return (
          <tr
            id={`navigator-compositenavinspectedwellinforendrer-tr-${inspSet.id}`}
            className="comNavTr"
            key={inspSet.id}
          >
            <th id={`navigator-compositenavinspectedwellinforendrer-th-${inspSet.id}`}>
              {inspSet.name}
            </th>
            {wellTDs}
          </tr>
        );
      }),
    );
  }

  function CompositeNavDRCRendrer() {
    const localDcrTRs = [];
    const dropTDs = [];
    const regionTDs = [];
    const cpNameTDs = [];

    if (compositeNavMap === undefined || compositeNavMap.drciList === undefined) {
      return;
    }

    compositeNavMap.drciList.map((drci) => {
      let td = (
        <td id={`navigator-compositenavdrcrendrer-dropnumber-${drci.drc?.cpId ?? 'na'}`}>
          {drci.drc.dropNumber}
        </td>
      );
      dropTDs.push(td);
      td = (
        <td id={`navigator-compositenavdrcrendrer-regionname-${drci.drc?.cpId ?? 'na'}`}>
          {drci.drc.regionName}
        </td>
      );
      regionTDs.push(td);
      const cpName = getCPName(drci.drc.cpId, compositeNavMap.profileList);
      td = (
        <td id={`navigator-compositenavdrcrendrer-cpname-${drci.drc?.cpId ?? 'na'}`}>
          {cpName}
        </td>
      );
      cpNameTDs.push(td);
    });
    localDcrTRs.push(
      <tr id="navigator-compositenavdrcrendrer-tr-imagingsettings" className="comNavTr">
        <th
          id="navigator-compositenavdrcrendrer-th-imagingsettings"
          className="horizontal-padding"
        >
          Imaging Settings
        </th>
        {cpNameTDs}
      </tr>,
    );
    localDcrTRs.push(
      <tr id="navigator-compositenavdrcrendrer-tr-region" className="comNavTr">
        <th
          id="navigator-compositenavdrcrendrer-th-region"
          className="horizontal-padding"
        >
          Region
        </th>
        {regionTDs}
      </tr>,
    );
    localDcrTRs.push(
      <tr id="navigator-compositenavdrcrendrer-tr-drop" className="comNavTr">
        <th id="navigator-compositenavdrcrendrer-th-drop" className="horizontal-padding">
          Drop
        </th>
        {dropTDs}
      </tr>,
    );
    setDcrTRs(localDcrTRs);
  }

  async function fetchCompositNavMap() {
    const expService = new ExperimentService();
    const navigatorMap: NavigatorMapDTO = await expService.getCompositeNavigatorData(
      exp.experimentId,
    );
    if (navigatorMap === null) {
      return null;
    }
    compositeNavMap = navigatorMap;
    CompositeNavInspectedWellInfoRendrer();
    CompositeNavDRCRendrer();
    setRefreshUI(!refreshUI);
  }

  function changeThTabIndex(index) {
    navigatorThumbnailTabIndex = index;
    setThTabIndex(index);
  }

  useEffect(() => {
    if (isNullOrUndefined(compositeNavMap) || _selectExpSaved !== lastForcedFetch) {
      fetchCompositNavMap();
      setLastForcedFetch(_selectExpSaved);
    }
  }, [_selectExpSaved]);

  useEffect(() => {
    CompositeNavInspectedWellInfoRendrer();
    CompositeNavDRCRendrer();
  }, [wellNavigationDetails, allWellImagingParameterDetails]);
  useEffect(() => {
    if (allWellImagingParameterDetails?.length >= 1 && moveToCanvas) {
      setMoveToCanvas(false);
      _setIsMoveToCompareTab(true);
    } else {
      _setIsMoveToCompareTab(false);
    }
  }, [allWellImagingParameterDetails]);

  return (
    <NavigatorSidePanel
      exp={exp}
      changeThTabIndex={changeThTabIndex}
      thTabIndex={thTabIndex}
      setMoveToCanvas={setMoveToCanvas}
      roi={rois}
      inspTRs={inspTRs}
      dcrTRs={dcrTRs}
    />
  );
}
